import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import type {ReplyTarget} from "download/reply/replyPrompt";
import {Button, DialogContent, Typography} from "@mui/material";
import {useLocale, useMessages} from "i18n";
import ExpiresAfter from "domain/ExpiresAfter";
import {useAuthenticatedSessionSnapshot, useIsLoggedIn, useUser} from "UserProvider";
import {ToastContext} from "../Toast";
import Consent from "../terms/Consent";
import {useTermsGate} from "../terms/useTermsGate";
import {signInWithToken, authenticatedSessionUid} from "../auth/authProviders";
import {useAuthoritativeSessionWait} from "../auth/useAuthoritativeSessionWait";
import {useEmailCodeLogin} from "../login/useEmailCodeLogin";
import {emailCodeLoginErrorMessage} from "../login/authErrorMessages";
import {objectionAbove} from "../login/loginViews";
import {useSignupObjection} from "../marketing/useSignupObjection";
import VerificationStep from "./components/VerificationStep";
// Type only: the TransferService implementation (and its transitive web-peer/STOMP/crypto/upload
// stack) is dynamically imported on first interaction — see `transfer()` — so it never lands in the
// eager landing bundle (perf #97). Importing the types erases at compile time and pulls no runtime code.
import type {TransferService, UploadRequest} from "../core/transfer/TransferService";
import type {SelectedFile} from "../core/transfer/selection";
import {setComposerFileCount} from "../core/transfer/composerSelection";
import {DeliveryNotice, deliveryNoticeMessage} from "../core/transfer/deliveryOutcome";
import {SelectionLosses, selectionLossMessage} from "../core/transfer/selectionLosses";
import {toFileMeta, UploadAbortedError} from "../core/upload/uploadContract";
import {requestModeRequests, storedFilesRequests} from "./composerArrivalRequests";
import {type StoredFile, storedFilesListFacts, summaryFilesOf} from "./storedFiles";
import {composerArrival} from "./composerArrival";
import {fileListFactsOf} from "./overLimit/fileListRules";
import type {OverLimitPricing} from "./overLimit/overLimitOffer";
import {PeerActivityListener} from "../core/p2p/peerActivity";
import {Mode, planOf} from "./gating";
import {expiryDeadline} from "home/expiryDeadline";
import {resolveDropArea} from "./dropAreaGate";
import {PageDropLayer} from "./components/PageDropLayer";
import {TransferPanel, TransferPayload} from "./components/TransferPanel";
import {ProgressCard} from "./components/ProgressCard";
import {useProgressReadout} from "./components/useProgressReadout";
import {emptyProgress, UploadProgress} from "./components/progressMath";
import {SuccessCard} from "./components/SuccessCard";
import {completionAction} from "games/gameSession";
import {useUploadGame} from "games/useTransferGame";
import {hostedGameStage} from "./hostedGameStage";
import {SuccessGiftFrame} from "home/components/SuccessGiftFrame";
import {composingOfferCandidates, type PeelCandidate} from "peel/peelTagSlot";
import {couponTransferId} from "offers/ladder/currentLadderOffer";
import {usePersonalizationDraft} from "./personalization/usePersonalizationDraft";
import {effectivePersonalizationAccent} from "./personalization/personalizationAccent";
import {SuccessPersonalization} from "./components/SuccessPersonalization";
import {useSenderChoice} from "./components/useSenderChoice";
import type {SenderCompany} from "sender/senderChoice";
import {
	personalizationRungSettles, successPersonalizationEntry,
} from "./personalization/successPersonalization";
import type {AttachedPersonalization} from "./personalization/personalizationDraft";
import {personalizationEvents, type PersonalizationMoment} from "./personalization/personalizationEvents";
import {sendEvents} from "./sendEvents";
import {noteTransferCreated} from "account/purchases/unusedPurchasesCache";
import {wirePersonalizationTelemetry} from "./personalization/personalizationEventBeacon";
import {
	isHostedSelectionOverCap, refusesAboveEveryPlan, recipientEmails, sameEmailAddress,
	usesHostedUpload, validateRecipientSelection,
} from "./components/createFormLogic";
import {hostedUploadLimitLabel} from "../pricing/planEntitlements";
import {recipientLimitMessage} from "./components/recipientLimitMessage";
import BaseDialog from "../components/BaseDialog";
import {VisuallyHiddenLiveRegion} from "../components/Accessibility.styles";
import {PickerDialogActions} from "./components/PickerDialogActions.styles";
import {
	DeliveryResumeOrchestrator,
} from "./deliveryResumeOrchestrator";
import {
	classifySenderFailure,
	planRefusalPricing,
	reportSenderFailure,
	SenderFailureContext,
	senderFailureText,
	type SenderFailure,
} from "./senderFailures";
import {usePendingDeliveryNavigationGuard} from "./usePendingDeliveryNavigationGuard";
import {useBeforeUnloadWarning} from "./useBeforeUnloadWarning";
import {useResumableUpload} from "./resume/useResumableUpload";
import {AbandonedUploadCard, ResumeUploadCard} from "./components/ResumeUploadCard";
import type {DiscardOutcome, KeptUpload} from "../core/upload/resume/keptUploads";
import {sessionExpireAfter, sessionSummary} from "../core/upload/resume/uploadSession";
import {afterDiscard, continuedRetention, keptSendOf, keptUploadEnd, type ResumeProblem} from "./resume/resumeOutcomes";
import {keptUploadsOwnerOf} from "./resume/forgetKeptUploads";
import type {SessionJournal} from "../core/upload/resume/uploadSessionJournal";
import type {ConnectionState} from "../core/upload/connectionState";
import {AuthenticatedSessionState} from "../auth/AuthenticatedSessionWaiter";
import {advanceTransferCreation, settledCreationAction} from "./transferCreationAuth";
import {SingleFlight} from "../utils/SingleFlight";
import {
	Backdrop, Card, ConsentDock, DeliveryErrorDetail, DeliveryRetryActions, DeliveryRetryCopy,
	DeliveryRetryRoot, DeliveryRetryTitle, FlowRoot, PanelSlot, RetryDeliveryButton,
	StartOverButton,
} from "./CreateFlow.styles";

/**
 * v3 sender flow (epic #17, tickets #19/#20) — the create → drag → progress → success state machine.
 *
 * Fully self-contained: owns the selected files, the whole-page drop overlay (via
 * {@link PageDropLayer}) and the {@link TransferService} instance. HomePage (ticket #23) never
 * reaches into those internals; it only says when a destination covers this exact persistent flow,
 * which makes hidden interaction passive without discarding the draft (ui#1061).
 *
 * Machine: `create` —submit→ `progress` —resolve→ `success` —send another→ `create`
 *                       ├—delivery reject→ `deliveryRetry` —resume→ `success`
 *                       ├—stopped short of a refusal (connection lost past the retry window, storage lost
 *                       │  an upload, a file no longer readable)→ `resume` (kept in this browser, ui#3204)
 *                       └—other reject→ `create` (error toast — silent when the user cancelled)
 *          `create` —a kept upload on load→ `resume` —continue→ `progress` —resolve→ `success`
 *                                              └—discard→ `abandoned` —send files→ `create`
 *          `create` —a kept upload ended past saving on load→ `abandoned` (expired)
 * The whole-page drag overlay + drop handling require both the `create` phase (design:
 * `screen==='create'`) and an uncovered canvas — progress/success and covered destination canvases
 * neither raise the overlay nor accept drops.
 *
 * Ticket #20 wiring: byte progress streams from EncryptedUploadClient's listeners through
 * `TransferService.upload` into the local {@link UploadProgress} state for {@link ProgressCard};
 * the progress screen's Cancel triggers `TransferService.cancelUpload`, whose
 * {@link UploadAbortedError} rejection returns the machine to `create` with the files kept; the
 * instant success screen observes P2P recipients via `TransferService.onPeerActivity`.
 */

export type CreatePhase =
	| {name: "create"}
	| {name: "progress"; mode: Mode}
	| {
		name: "deliveryRetry";
		mode: Mode;
		expireAfter: ExpiresAfter;
		passwordProtected: boolean;
		maxDownloads?: number;
		initialRecipientEmails?: string[];
		failure: SenderFailure;
		busy: boolean;
		/** The link the delivery is retried for was made to stored files: nothing was uploaded (ui#3405). */
		storedLink?: boolean;
	}
	/**
	 * "Continue your upload" (kitecorp/transferify-ui#3204): an unfinished hosted upload this browser kept,
	 * offered after a reload or a closed tab, or straight after the connection stayed lost too long here.
	 */
	| {
		name: "resume";
		kept: KeptUpload;
		/** The files this page still holds (an upload interrupted here), used before the browser's kept copy. */
		pageFiles: File[] | null;
		problem: ResumeProblem | null;
		/** What the card is doing while its buttons wait: claiming and continuing it, or discarding it. */
		busy: "continuing" | "discarding" | null;
	}
	/** An unfinished upload that is over without being sent, said plainly (ui#3204). */
	| {name: "abandoned"; reason: "discarded" | "gone" | "expired"}
	| {
		name: "success";
		mode: Mode;
		url: string;
		transferId?: string;
		expiresAtMs: number | null;
		passwordProtected: boolean;
		maxDownloads?: number;
		initialRecipientEmails?: string[];
		/** Who this send actually reached (ui#369); absent when it notified nobody. */
		deliveryNotice?: DeliveryNotice;
	};

type SuccessPhase = Extract<CreatePhase, {name: "success"}>;

interface Props {
	/** True while a destination CardSheet covers this persistent flow without unmounting it. */
	covered?: boolean;
	replyTarget?: ReplyTarget | null;
	replyActive?: boolean;
}

const CreateFlow = React.forwardRef<HTMLDivElement, Props>(function CreateFlow(
	{covered = false, replyTarget = null, replyActive = false},
	ref,
) {
	const {showErrorToast, showSuccessToast, showWarningToast} = useContext(ToastContext);
	// ui#1170: the consent gate is part of the drop area now, so the flow that owns the drop area owns
	// it. It opens itself on arrival for a sender who has not accepted, and `validate` re-reads the
	// stored acceptance at the submit boundary exactly as it always did.
	const terms = useTermsGate();
	const {banner, common, login, pricing, sender} = useMessages();
	const {locale} = useLocale();
	// The code login establishes only the account session. It never owns or resumes a transfer payload;
	// the still-mounted form is submitted again explicitly after authentication (ui#246/api-service#144).
	const [user] = useUser();
	const isLoggedIn = useIsLoggedIn();
	const [authenticatedSession, readLatestSession] = useAuthenticatedSessionSnapshot();
	const codeLogin = useEmailCodeLogin(signInWithToken, authenticatedSession, readLatestSession, authenticatedSessionUid);
	// A Send pressed by a cached sign-in before Firebase has answered waits here (ui#2088), shown as
	// pressed through the same busy state the code login uses.
	const sessionWait = useAuthoritativeSessionWait(authenticatedSession.state);
	const codeAnnouncementMessage = codeLogin.announcementMessage(sender.verification.announcement);
	const codeDeliveryMessage = codeLogin.deliveryMessage(sender.verification);
	const codeLoginError = codeLogin.error === null
		? null
		: emailCodeLoginErrorMessage(codeLogin.error, login.code);
	useEffect(() => {
		if (codeLogin.phase.step === "request" && codeLoginError !== null) {
			showErrorToast(codeLoginError);
		}
	}, [codeLogin.phase.step, codeLoginError, showErrorToast]);
	// ui#94: the selection carries each file's folder path; raw `File`s derive from it where needed.
	const [selection, setSelection] = useState<SelectedFile[]>([]);
	/**
	 * Files the account already stores, handed over by the Files tab's "New transfer with these files"
	 * (kitecorp/transferify-ui#3405): while set, the composer holds these instead of a selection, and Send makes a
	 * NEW LINK to them (`TransferService.createStoredLink`) with nothing uploaded. Null while composing as usual.
	 */
	const [storedFiles, setStoredFiles] = useState<readonly StoredFile[] | null>(null);
	// Read at the moment of sending, like the selection: a Send held for the restored session runs from an older
	// render's closure (ui#2088).
	const storedFilesRef = useRef(storedFiles);
	storedFilesRef.current = storedFiles;
	/** A new link to stored files is being made: Send stays pressed, there is no progress to show. */
	const [linkingStored, setLinkingStored] = useState(false);
	const [phase, setPhase] = useState<CreatePhase>({name: "create"});
	// The code step's Verify creates the visitor's account, so it offers the marketing objection like
	// every other control that can (kitecorp/transferify-ui#3503), and the tick starts fresh each time the
	// step comes up.
	const codeStepOpen = phase.name === "create" && codeLogin.phase.step === "confirm";
	const codeStepObjection = useSignupObjection(codeStepOpen && objectionAbove("sendFlowCode") !== null, codeStepOpen);
	const [progress, setProgress] = useState<UploadProgress>(emptyProgress([]));
	const [startOverConfirmOpen, setStartOverConfirmOpen] = useState(false);
	/**
	 * The last submit was refused because the account's LIVE transfers already fill the plan's
	 * active-storage cap (ui#1664, classified by the api's stable `errorId`). Rendered by the panel
	 * as the {@link ./components/ActiveStorageNotice} strip instead of the generic failure toast;
	 * cleared when the next submit starts, because deleting a transfer, upgrading or paying for this
	 * one send (kitecorp/transferify-ui#3046) — the strip's exits — are the only actions that can
	 * change the api's answer, and the next submit is where the api gives it.
	 */
	const [activeStorageRefused, setActiveStorageRefused] = useState(false);
	/**
	 * The wall the last registration was refused on, when only a plan can take the send past the free size: an Instant
	 * send (`error.transfer.instant.planRequired`, kitecorp/transferify-api-service#3005, Q5) or a hosted upload
	 * (`error.transfer.free.maxUpload`, kitecorp/transferify-ui#3800). The composer answers it by opening the sized plan
	 * dialog, the smallest plan that holds the drop chosen, once the wall holds the plan rows, then drops this; the next
	 * submit clears it too, so a refusal the sender walked away from never opens a dialog over a later selection.
	 */
	const [planRequiredRefused, setPlanRequiredRefused] = useState<OverLimitPricing | null>(null);
	usePendingDeliveryNavigationGuard(
		phase.name === "deliveryRetry",
		sender.deliveryRetry.confirmStartOver.body,
	);
	// The page carries the upload: leaving it stops the bytes (kitecorp/transferify-ui#1301).
	useBeforeUnloadWarning(phase.name === "progress");
	/** What the upload's requests are doing, for the progress card's title and note (ui#3204). */
	const [connection, setConnection] = useState<ConnectionState>("connected");
	/** This browser keeps the running upload, so the card may promise a continue after a closed tab (ui#3204). */
	const [continuable, setContinuable] = useState(false);
	const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
	/**
	 * The signed-in account a kept upload belongs to. Read through a ref at the moment of sending, like the
	 * user below, because a Send held for the restored session runs from an older render's closure.
	 */
	const ownerUid = authenticatedSession.state === AuthenticatedSessionState.Authenticated ? authenticatedSession.uid : null;
	const ownerUidRef = useRef(ownerUid);
	ownerUidRef.current = ownerUid;
	// Undefined while the session is still being restored: nobody can be told apart from a sender yet.
	const resumable = useResumableUpload(keptUploadsOwnerOf(authenticatedSession));
	/** Kept uploads this page has already finished, continued or discarded: never offered again from a stale read. */
	const settledSessions = useRef(new Set<string>());
	// A kept upload is its account's alone: when that account signs out or another signs in, in this tab, the
	// card it was showing goes, before the next account could continue, discard or read it (ui#3204).
	const shownOwner = useRef(ownerUid);
	useEffect(() => {
		if (shownOwner.current === ownerUid) {
			return;
		}
		shownOwner.current = ownerUid;
		setDiscardConfirmOpen(false);
		setPhase(current => current.name === "resume" || current.name === "abandoned" ? {name: "create"} : current);
	}, [ownerUid]);
	// ui#1305: the login modal lives outside this subtree and has to know, before it starts a full-page
	// provider redirect, that this document is holding files that cannot survive one. Published from an
	// EFFECT on the rendered length rather than from `changeSelection`, because four other paths clear
	// the selection (delivery-retry capture, start-over, send-another, unmount) and any of them could
	// forget a second call — the effect cannot. Unmounting publishes 0: no composer, nothing to lose.
	useEffect(() => {
		setComposerFileCount(selection.length);
		return () => setComposerFileCount(0);
	}, [selection.length]);

	/**
	 * What this send attached, or `null` for a transfer that went out plain (ui#2411).
	 *
	 * A boolean before, which recorded only that a look rode along: a sender who signed their transfer in
	 * the builder BEFORE pressing Send then got no entry on the success card, while one who typed the
	 * same name AFTER sending kept theirs. The RUNG answers whether a branded look went out, which is the
	 * question {@link personalizationRungSettles} asks of both moments; the REQUEST is what the success
	 * composer starts from, because attaching a look there replaces the snapshot rather than merging
	 * into it.
	 */
	const sentPersonalization = useRef<AttachedPersonalization | null>(null);
	const [builderAppeared, setBuilderAppeared] = useState(false);
	// Fetched once there is a transfer to personalize, rather than on mount: the landing page renders
	// this flow for every visitor, and a signed-out sender's offer call would only 401. Read with the
	// first file rather than when the builder opens, so the builder has its catalogue the moment it appears.
	// The landing's composer is the one a Halloween slide press or a `?tema=` arrival hands its scene to
	// (kitecorp/transferify-ui#3776), so it listens for those requests; the success card's composer does not.
	const personalise = usePersonalizationDraft(builderAppeared || selection.length > 0, true);
	// Who this transfer goes out as, a person or a company (kitecorp/transferify-ui#3186), held here beside the look
	// rather than in the panel: the panel unmounts while the transfer is sent, and a send that is cancelled or fails
	// comes back to it, where the retry must still go out as the company the sender chose.
	const senderChoice = useSenderChoice(user.company);
	/** The company the last send went out as, or null for a person: the success card's builder names it. */
	const sentCompany = useRef<SenderCompany | null>(null);
	// Where the slide-out cards render (ui#2468, ui#2278): a bare warning candidate in the peel frame
	// around the card, so a card is the panel's SIBLING and can slide out from behind it. TransferPanel
	// owns the data and portals both cards in; a callback ref because the host exists only once the frame
	// does. ONE candidate for the family — the slot shows a single candidate, and the two cards are
	// mutually exclusive by their own rule rather than by the slot's.
	const [slideOutHost, setSlideOutHost] = useState<HTMLDivElement | null>(null);
	const slideOutCandidates = useMemo<PeelCandidate<React.ReactNode>[]>(() => [
		{id: "download-limit", family: "warning", priority: 0, bare: true, node: <div ref={setSlideOutHost} data-slide-out-host=""/>},
	], []);
	// The trial-expiry offer beside the card (ui#2949): the same host-and-portal pair, in a slot of its
	// own so it never competes with the slide-out cards' host for the one tag a slot shows. The offer
	// announces itself from the panel, so the frame hangs a tag only while there is one to show.
	const [besideOfferElement, setBesideOfferElement] = useState<HTMLDivElement | null>(null);
	const [besideOffer, setBesideOffer] = useState<{dismiss: () => void} | null>(null);
	const besideOfferHost = useMemo(() => ({element: besideOfferElement, announce: setBesideOffer}), [besideOfferElement]);
	const besideOfferCandidates = useMemo(() => composingOfferCandidates<React.ReactNode>(
		besideOffer === null ? null : <div ref={setBesideOfferElement} data-beside-offer-host=""/>,
		() => besideOffer?.dismiss(), banner.trialExpiryOffer.dismissAria), [besideOffer, banner.trialExpiryOffer.dismissAria]);
	/**
	 * The funnel's counters leave the browser from here (owner's 2026-08-27 rule: business logic emits
	 * domain events, telemetry SUBSCRIBES to them).
	 *
	 * This flow is the funnel's owner — it survives the create/progress/success machine and the four
	 * destination sheets — so a subscription here covers every counter the emitter can produce. The
	 * builder and the panel each unmount partway through, which is why neither of them holds it.
	 */
	useEffect(() => wirePersonalizationTelemetry(personalizationEvents), []);

	const raiseBuilder = useCallback((moment: PersonalizationMoment) => {
		setBuilderAppeared(true);
		personalizationEvents.builderOpened(moment);
	}, []);

	/** The panel's builder counters. The success flow owns the LATE ones. */
	const openBuilderFromPanel = useCallback(() => raiseBuilder("SEND"), [raiseBuilder]);
	const countBuilderCommitted = useCallback(() => personalizationEvents.builderCompleted("SEND"), []);

	const uploading = phase.name === "progress";
	/**
	 * The upload's smoothed rate and ETA, owned HERE rather than by the progress card.
	 *
	 * The card is a dumb view and this model outlives one render of it: `active` is what keeps its timer
	 * off for the landing-page visitors this flow also renders for, and what forgets one transfer before
	 * the next — a lifecycle the flow owns and the card does not see. The card takes the sample as a
	 * prop and shows "65.0 MB/s · 24s left" from it.
	 */
	const uploadReadout = useProgressReadout(progress, uploading);
	/**
	 * The game on a hosted transfer (kitecorp/transferify-ui#2872). An upload that finishes while the sender is in
	 * the game parks its success here until they press on; one that finishes with the game closed goes straight to
	 * the success card, exactly as before the game existed. The slot lives on into that card, which offers the same
	 * game at the end of its share row (kitecorp/transferify-ui#3388).
	 */
	const uploadGame = useUploadGame(uploadReadout, hostedGameStage(phase));
	const [heldSuccess, setHeldSuccess] = useState<SuccessPhase | null>(null);
	const continueToSuccess = () => {
		if (heldSuccess !== null) {
			uploadGame.close();
			setPhase(heldSuccess);
			setHeldSuccess(null);
		}
	};

	// One service per flow instance; owns upload orchestration + P2P seeding for the selection.
	const transferRef = useRef<TransferService | null>(null);
	const deliveryResumeRef = useRef<DeliveryResumeOrchestrator | null>(null);
	// Final validation runs again on the explicit post-authentication submit. Read the latest account
	// at that action boundary so a newly inactive subscription resolves Free.
	const userRef = useRef(user);
	userRef.current = user;
	// Mirrors the latest selection so an in-flight lazy load can't apply a stale one (see changeSelection).
	const selectionRef = useRef<SelectedFile[]>([]);

	/**
	 * Lazily loads the transfer machinery (TransferService → web-peer/STOMP/crypto/upload clients) on
	 * first interaction, keeping it out of the eager landing bundle (perf #97). The instance is cached
	 * after the first call; concurrent callers that race the dynamic import still share one instance
	 * (the `??=` runs only after the await, so the first assignment wins).
	 */
	const transfer = async (): Promise<TransferService> => {
		if (transferRef.current) return transferRef.current;
		const {TransferService} = await import("../core/transfer/TransferService");
		const service = (transferRef.current ??= new TransferService());
		deliveryResumeRef.current ??= new DeliveryResumeOrchestrator(service);
		return service;
	};

	const rawFiles = selection.map(entry => entry.file);
	// What the panel's file list renders: name/size plus the folder path as secondary text (ui#94), or the stored
	// files a new link is made to (kitecorp/transferify-ui#3405).
	const panelFiles = storedFiles !== null ? summaryFilesOf(storedFiles) : selection.map(({file, relativePath}) =>
		({name: file.name, size: file.size, relativePath}));
	// The selection's file-list facts, measured on the very declarations the upload init will be sent, and judged
	// before any over-limit payment step (kitecorp/transferify-ui#2930). Memoized on the selection, which is a new
	// array exactly when it changes: a folder drop is thousands of entries, and nothing else moves the facts.
	const fileList = useMemo(() => storedFiles !== null
		? storedFilesListFacts(storedFiles)
		: fileListFactsOf(selection.map(toFileMeta)), [selection, storedFiles]);

	const changeSelection = (next: SelectedFile[]) => {
		selectionRef.current = next;
		setSelection(next);
		// Push the selection into the (lazily-loaded) seeder; selectionRef holds the LATEST value so
		// rapid changes before the dynamic import resolves can never seed an older selection (perf #97
		// made transfer() async).
		void transfer().then(t => {
			t.selection = selectionRef.current;
		});
	};

	/** Drops/browses merge only while composing — never mid-upload or on the success screen. */
	const onSelectionChange = (next: SelectedFile[]) => {
		if (phase.name === "create") changeSelection(next);
	};

	/**
	 * What a drop or pick could not carry (ui#1308): empty subfolders, and files whose folder path
	 * cannot be stored and will arrive flat. Neither is preservable without an api change, so the
	 * sender is TOLD — the alternative is a recipient's zip that quietly differs from what was sent.
	 * The verdict and its wording are the domain's; this only shows the sentence it returns.
	 */
	const onSelectionLosses = (losses: SelectionLosses) => {
		const message = selectionLossMessage(losses, sender.toasts.losses);
		if (message !== null) showWarningToast(message);
	};

	const onRemoveFile = (index: number) => {
		if (storedFiles !== null) {
			// The last stored file removed leaves an empty composer, ready for a usual send.
			const remaining = storedFiles.filter((_, i) => i !== index);
			setStoredFiles(remaining.length > 0 ? remaining : null);
			return;
		}
		changeSelection(selection.filter((_, i) => i !== index));
	};

	/** Pre-submit checks that need toasts/terms — kept out of the presentational panel. */
	const validate = (payload: TransferPayload): boolean => {
		// The STORED acceptance stays the authoritative gate, re-read here rather than trusted from
		// render state — site data cleared in another tab must still stop a send, not just repaint.
		if (!terms.confirmAccepted()) {
			return false;
		}
		if (!selection.length && !storedFiles?.length) {
			showWarningToast(sender.toasts.selectFile);
			return false;
		}
		if (payload.delivery !== "link") {
			if (!payload.emailsFrom) {
				showWarningToast(sender.toasts.enterEmail);
				return false;
			}
			const recipientValidation = validateRecipientSelection(
				payload.recipientSelection, planOf(userRef.current));
			if (recipientValidation.count === 0) {
				showWarningToast(sender.toasts.enterRecipients);
				return false;
			}
			if (!recipientValidation.valid) {
				showErrorToast(recipientLimitMessage(
					planOf(userRef.current),
					pricing.planNames,
					pricing.compare.unlimited,
					sender.recipients,
					locale,
				));
				return false;
			}
		}
		return true;
	};

	/** What a send asks the transfer service for, from the composer's payload: an upload, or a new link to stored files. */
	const uploadRequestOf = (payload: TransferPayload, keepOnline: boolean): UploadRequest => {
		const baseRequest = {
			// The sender's note, from the ONE place it is written since ui#1814: the personalization
			// builder. It travels on the transfer as well as on the personalization because two
			// different readers need it — the delivery email renders this field, the branded download
			// page renders the personalization's — and both are the same string by construction.
			message: personalise.note,
			keepOnline,
			expireAfter: payload.expireAfter,
			password: payload.password,
			maxDownloads: payload.maxDownloads,
			notifyOnDownload: payload.notifyOnDownload,
			recoveryOptIn: payload.recoveryOptIn,
			keyMode: payload.keyMode,
			// api#461: the personalization is a CREATION field: the api takes its snapshot there. Every plan
			// includes the look (kitecorp/transferify#525), so no link is ever held for a payment.
			...(payload.personalization ? {personalization: payload.personalization.request} : {}),
			// kitecorp/transferify-ui#3186: the company the transfer is sent as, also a creation field.
			...(payload.senderCompany ? {senderCompany: payload.senderCompany} : {}),
			// ui#1454 / api#823: the paid over-cap allowance, NAMED on the init. Nothing is applied
			// server-side without it, so a purchase is spent on the send it was bought for and no
			// other.
			...(payload.overLimitAllowanceId
				? {overLimitAllowanceId: payload.overLimitAllowanceId}
				: {}),
		};
		// One request carries both target sets; TransferService fires /email and /group as needed
		// (skipping an empty set) after a single shared cloud upload (ui#210).
		return payload.delivery === "recipients"
			? {
				...baseRequest,
				transferType: "recipients",
				toEmails: payload.recipientSelection.emails,
				groupIds: payload.recipientSelection.groups.map(group => group.id),
			}
			: {...baseRequest, transferType: "link"};
	};

	const startTransfer = async (payload: TransferPayload) => {
		// The account as it stands NOW, never the render's: a Send held for the restored session (ui#2088)
		// reaches here from the closure of the render it was pressed in, after the session has answered
		// and possibly replaced the cached user. The same read the plan below has always made.
		const account = userRef.current;
		const accountEmail = account.isLoggedIn ? account.email : null;
		if (!payload.emailsFrom || !sameEmailAddress(payload.emailsFrom, accountEmail)) {
			showErrorToast(sender.toasts.senderAccountMismatch);
			return;
		}
		const hostedUpload = usesHostedUpload(payload.mode, payload.delivery);
		const currentFiles = selectionRef.current.map(entry => entry.file);
		// What the send weighs: the stored files a new link is made to, or the files picked.
		const stored = storedFilesRef.current;
		const sentFiles: ReadonlyArray<{size: number}> = stored ?? currentFiles;
		const currentPlan = planOf(account);
		// Recheck after sign-in: files or account state can change while the code gate is open.
		// This sums original File sizes, so encryption chunks and ciphertext overhead never consume quota.
		//
		// THE ENFORCEMENT SURVIVED ui#1454 — it stopped being a dead end, it did not go away. An over-cap
		// send is admitted here only when it NAMES a paid allowance; without one it is still refused, now
		// pointing at the purchase instead of at an upgrade. Coverage is not re-decided here: the api owns
		// it (`error.transfer.overLimit.notConsumable`), and a second opinion computed from a plan that
		// resolved a moment ago is exactly how a sender who has paid gets refused by their own browser.
		// ABOVE THE LARGEST PLAN no payment helps (kitecorp/transferify-api-service#3005): the api refuses the size for every
		// tier, so this names the limit rather than pointing at a purchase or an upgrade. A pass that already covers the
		// size still goes through, as the strip's `paid` rung says.
		if (refusesAboveEveryPlan(payload, sentFiles)) {
			showErrorToast(sender.overLimit.aboveLargestPlan);
			return;
		}
		if (isHostedSelectionOverCap(payload.mode, payload.delivery, currentPlan, sentFiles)
			&& payload.overLimitAllowanceId === undefined) {
			showErrorToast(currentPlan === "free"
				? sender.overLimit.paymentRequired
				: sender.hostedLimitExceeded(
					pricing.planNames[currentPlan],
					hostedUploadLimitLabel(currentPlan, pricing.compare.unlimited, locale),
				));
			return;
		}
		// The UI snapshot is advisory for group membership, but still blocks obvious over-limit sends.
		// The API expands current persisted memberships and remains authoritative for TOCTOU changes.
		if (payload.recipientSelection !== null) {
			const recipientValidation = validateRecipientSelection(payload.recipientSelection, currentPlan);
			if (recipientValidation.count === 0) {
				showErrorToast(sender.toasts.enterRecipients);
				return;
			}
			if (!recipientValidation.valid) {
				showErrorToast(recipientLimitMessage(
					currentPlan,
					pricing.planNames,
					pricing.compare.unlimited,
					sender.recipients,
					locale,
				));
				return;
			}
		}
		// A recipients send carrying nobody is refused by the count check above, and a link carrying
		// recipients is unrepresentable in {@link TransferPayload} (ui#890) — so the pairing needs no
		// third check here. It used to have one; deriving the delivery took away its ability to fail.
		const effectiveMode: Mode = hostedUpload ? "upload" : "instant";
		const initialRecipientEmails = payload.recipientSelection === null
			? undefined
			: recipientEmails(payload.recipientSelection);
		// A fresh attempt gets the api's fresh answer: the sender may have deleted a transfer or
		// upgraded since the refusal, and a stale strip would contradict a send that now succeeds.
		setActiveStorageRefused(false);
		setPlanRequiredRefused(null);
		sentPersonalization.current = payload.personalization ?? null;
		sentCompany.current = payload.senderCompany ?? null;
		if (stored !== null) {
			// A new link to stored files (kitecorp/transferify-ui#3405): no progress to show, straight to success.
			sendEvents.started();
			await linkStoredFiles(payload, stored, initialRecipientEmails);
			return;
		}
		setProgress(emptyProgress(currentFiles));
		setConnection("connected");
		setContinuable(false);
		setPhase({name: "progress", mode: effectiveMode});
		// A send has begun: every refusal above has been passed and this flow is committing to the
		// transfer. One domain fact, named and dropped — this line does not know that the recipient funnel
		// subscribes to it, or that anyone does (owner's 2026-08-27 rule; see `./sendEvents`).
		sendEvents.started();
		let service: TransferService | null = null;
		// Keeps a hosted send continuable after a reload or a closed tab (ui#3204); an instant one has no bytes to keep.
		const journal = hostedUpload ? await journalFor(payload, initialRecipientEmails) : undefined;
		try {
			const keepOnline = hostedUpload;
			service = await transfer();
			// Seed the exact submitted selection incl. folder paths (defensive: the selection-change
			// seeding above is async; selectionRef holds the latest, ui#94).
			service.selection = selectionRef.current;
			const uploadRequest = uploadRequestOf(payload, keepOnline);
			const response = await service.upload(uploadRequest, progressListeners,
				{journal, onConnection: setConnection});
			// The transfer exists, so the one-off it may have spent — the named allowance, a branding
			// credit — is no longer unused, and the purchase "Use it" chose is done with (ui#3004).
			noteTransferCreated();
			arriveAtSuccess({
				name: "success",
				mode: effectiveMode,
				// The SERVER's link verbatim. It is the only party that knows where this transfer
				// canonically lives: the origin is snapshotted at creation from the sender's brand AND
				// their claimed subdomain, so a claimed sender's link is their own host
				// (kitecorp/transferify-api-service#350/#458). Rewriting it to the browsing origin here
				// would strip that host back off and display a link the sender never gets to hand out.
				url: response.url,
				transferId: response.transferId,
				expiresAtMs: expiryDeadline(payload.expireAfter, Date.now()),
				passwordProtected: Boolean(payload.password),
				maxDownloads: payload.maxDownloads,
				initialRecipientEmails,
				deliveryNotice: response.delivery,
			}, keepOnline, response.delivery);
		} catch (e) {
			// A user cancel (progress "Cancel" → cancelUpload) is not an error — return silently.
			if (e instanceof UploadAbortedError) {
				setPhase({name: "create"});
				return;
			}
			// Stopped short of a refusal, and this browser kept the upload: offer to continue it, with the files
			// this page still holds when they can still be read (ui#3204). A delivery left to retry is not offered.
			if (journal?.sessionId && await offerKeptUpload(e, journal.sessionId,
				selectionRef.current.map(entry => entry.file), service?.hasPendingDelivery === true)) {
				return;
			}
			failedSend(e, service, {
				mode: effectiveMode,
				expireAfter: payload.expireAfter,
				passwordProtected: Boolean(payload.password),
				maxDownloads: payload.maxDownloads,
				initialRecipientEmails,
			});
		}
	};

	/**
	 * Makes a new link to the stored files the composer holds (kitecorp/transferify-ui#3405): one create call with
	 * the files' ids, nothing uploaded, then the recipients mailed as after an upload. The composer stays on its
	 * card with Send pressed while it runs, and opens the success card the hosted flow shows. A failure is a
	 * send's failure (`failedSend`), a delivery left to retry included.
	 */
	const linkStoredFiles = async (payload: TransferPayload, files: readonly StoredFile[],
								   initialRecipientEmails: string[] | undefined) => {
		setLinkingStored(true);
		let service: TransferService | null = null;
		try {
			service = await transfer();
			const response = await service.createStoredLink(uploadRequestOf(payload, true), files.map(file => file.id));
			noteTransferCreated();
			setPhase({
				name: "success",
				mode: "upload",
				url: response.url,
				transferId: response.transferId,
				expiresAtMs: expiryDeadline(payload.expireAfter, Date.now()),
				passwordProtected: Boolean(payload.password),
				maxDownloads: payload.maxDownloads,
				initialRecipientEmails,
				deliveryNotice: response.delivery,
			});
			const skipped = response.delivery === undefined ? null : deliveryNoticeMessage(response.delivery, sender.success.delivery);
			if (skipped !== null) {
				showWarningToast(skipped);
			} else {
				showSuccessToast(sender.toasts.linkReady);
			}
		} catch (e) {
			failedSend(e, service, {
				mode: "upload",
				expireAfter: payload.expireAfter,
				passwordProtected: Boolean(payload.password),
				maxDownloads: payload.maxDownloads,
				initialRecipientEmails,
				storedLink: true,
			});
		} finally {
			setLinkingStored(false);
		}
	};

	/** The upload's byte progress into the state the progress card reads. */
	const progressListeners = {
		onProgress: (bytesSent: number, totalBytes: number) =>
			setProgress(prev => ({...prev, bytesSent, totalBytes})),
		onFileProgress: (fileIndex: number, fileBytesSent: number) =>
			setProgress(prev => {
				const perFileBytes = [...prev.perFileBytes];
				perFileBytes[fileIndex] = fileBytesSent;
				return {...prev, perFileBytes};
			}),
	};

	/** A send that went through: its success card, held while the sender plays, and the toast that says so. */
	const arriveAtSuccess = (success: SuccessPhase, keepOnline: boolean, delivery: DeliveryNotice | undefined) => {
		if (success.mode === "upload") {
			uploadGame.transferCompleted();
		}
		if (success.mode === "upload" && completionAction(uploadGame.isOpen()) === "hold") {
			setHeldSuccess(success);
		} else {
			setPhase(success);
		}
		// A send that skipped an opted-out recipient is not the plain success the upload toast
		// claims (ui#369) — the success screen carries the same sentence, so it also outlives this.
		const skipped = delivery === undefined ? null : deliveryNoticeMessage(delivery, sender.success.delivery);
		if (skipped !== null) {
			showWarningToast(skipped);
		} else {
			showSuccessToast(keepOnline ? sender.toasts.uploaded : sender.toasts.instantActive);
		}
	};

	/**
	 * A send that failed: the delivery retry when the bytes are up and only the recipients are left, the
	 * storage-full strip for that refusal, else the composer and a toast that names the failure.
	 */
	const failedSend = (error: unknown, service: TransferService | null,
						retry: Omit<Extract<CreatePhase, {name: "deliveryRetry"}>, "name" | "failure" | "busy">) => {
		const resume = service === null ? null : deliveryResumeRef.current;
		const deliveryPending = resume?.captureFailure() === "pending";
		const context = deliveryPending ? SenderFailureContext.Delivery : SenderFailureContext.Upload;
		const failure = classifySenderFailure(error, context);
		reportSenderFailure(context, failure);
		if (deliveryPending) {
			// TransferService released its File references after completion. Mirror that in React so
			// the retry screen retains neither the form nor its raw File/password state.
			selectionRef.current = [];
			setSelection([]);
			setStoredFiles(null);
			// A game open on the progress card closes with it. Its slot carries on through the retry, so the
			// success card after it offers the same game and the transfer reports one offer (kitecorp/transferify-ui#3388).
			uploadGame.close();
			setPhase({name: "deliveryRetry", ...retry, failure, busy: false});
			return;
		}
		setPhase({name: "create"});
		if (failure.messageKey === "activeStorageFull") {
			// The panel's ActiveStorageNotice strip is the message for this refusal: it names the cap and carries the
			// exits (delete an old transfer, upgrade) that a toast cannot.
			setActiveStorageRefused(true);
			return;
		}
		const refusedFor = planRefusalPricing(failure.messageKey);
		if (refusedFor !== null) {
			// The plan dialog is the message for this refusal on either wall (Instant, or a hosted upload over the free
			// cap): the composer opens it, the smallest fitting plan chosen, as soon as the wall holds the plans the api
			// offers. A toast would repeat what the dialog says.
			setPlanRequiredRefused(refusedFor);
			return;
		}
		showErrorToast(senderFailureText(failure, sender.errors, sender.deliveryRetry.retryAfter));
	};

	/**
	 * The journal that keeps a hosted send continuable after a reload or a closed tab
	 * (kitecorp/transferify-ui#3204), built from what only the composer knows: whose send it is, what was
	 * picked, who it goes to and what its success card says. Undefined with nobody signed in to own it, or
	 * when the runtime cannot load; the send then runs exactly as before, just not continuable.
	 */
	const journalFor = async (payload: TransferPayload,
							  initialRecipientEmails: string[] | undefined): Promise<SessionJournal | undefined> => {
		const owner = ownerUidRef.current;
		if (owner === null) {
			return undefined;
		}
		try {
			const runtime = await import("../core/upload/resume/resumeRuntime");
			return runtime.newUploadJournal({
				ownerUid: owner,
				selection: selectionRef.current,
				...keptSendOf(payload, personalise.note, initialRecipientEmails),
			}, setContinuable);
		} catch {
			return undefined;
		}
	};

	/** A kept upload read back by id, with its kept files; null once it is gone, or when it cannot be read. */
	const readKeptUpload = async (sessionId: string): Promise<KeptUpload | null> => {
		try {
			const runtime = await import("../core/upload/resume/resumeRuntime");
			return await runtime.keptUpload(sessionId);
		} catch {
			return null;
		}
	};

	/**
	 * After a send failed: shows the card again for an upload the browser still keeps, saying why
	 * (`keptUploadEnd`); false when the failure ended it, or left a delivery to retry.
	 */
	const offerKeptUpload = async (error: unknown, sessionId: string, pageFiles: File[] | null,
								   deliveryPending: boolean): Promise<boolean> => {
		const kept = deliveryPending ? null : await readKeptUpload(sessionId);
		const end = keptUploadEnd(error, {stillKept: kept !== null, deliveryPending});
		if (end.kind !== "kept" || kept === null) {
			return false;
		}
		setPhase({name: "resume", kept, pageFiles: end.pageFilesUsable ? pageFiles : null, problem: end.problem, busy: null});
		return true;
	};

	/**
	 * Continues a kept upload this page claimed, with its files (ui#3204): the composer shows them on the progress
	 * card as it would a fresh send, the transfer service picks the upload up where storage stopped and delivers
	 * it, and the success card opens on the link. It ends as a fresh send ends; a cancel, a gone transfer and a
	 * session still kept after a failure have answers of their own (`keptUploadEnd`). The claim's lock is held by
	 * the session's journal until the upload ends: through a delivery still to retry, so no other tab continues it
	 * meanwhile, and given back at once by a failure, so this page can claim the upload again.
	 */
	const continueUpload = async (claim: {kept: KeptUpload; release: () => void}, files: File[]) => {
		const {record} = claim.kept.session;
		const summary = sessionSummary(claim.kept.session);
		const sessionFiles: SelectedFile[] = record.files.map((declared, index) => declared.relativePath === undefined
			? {file: files[index]}
			: {file: files[index], relativePath: declared.relativePath});
		changeSelection(sessionFiles);
		// The gauge opens where storage stopped, so the rate it measures starts from there too.
		setProgress({...emptyProgress(files), bytesSent: summary.uploadedBytes});
		setConnection("connected");
		setContinuable(true);
		setPhase({name: "progress", mode: "upload"});
		let service: TransferService | null = null;
		try {
			service = await transfer();
			const runtime = await import("../core/upload/resume/resumeRuntime");
			const response = await service.resumeUpload(claim.kept.session, files, progressListeners,
				{journal: runtime.journalContinuing(claim, setContinuable), onConnection: setConnection});
			settledSessions.current.add(record.id);
			noteTransferCreated();
			const retention = sessionExpireAfter(record);
			arriveAtSuccess({
				name: "success",
				mode: "upload",
				url: response.url,
				transferId: response.transferId,
				// The api counts the link's life from the upload's start, which was the first page.
				expiresAtMs: retention === null ? null : expiryDeadline(retention, record.createdAt),
				passwordProtected: record.display.passwordProtected,
				maxDownloads: record.display.maxDownloads,
				initialRecipientEmails: record.display.initialRecipientEmails,
				deliveryNotice: response.delivery,
			}, true, response.delivery);
		} catch (e) {
			// A delivery left to retry holds the lock through the parked journal until it is retried or discarded,
			// so no other tab continues the send and notifies the recipients twice.
			const deliveryPending = service?.hasPendingDelivery === true;
			if (!deliveryPending) {
				// The journal has given the lock back already on every path the client ends; this covers a failure
				// that came before the journal was given the upload at all.
				claim.release();
			}
			const kept = deliveryPending ? null : await readKeptUpload(record.id);
			const end = keptUploadEnd(e, {stillKept: kept !== null, deliveryPending});
			if (end.kind === "kept" && kept !== null) {
				setPhase({name: "resume", kept, pageFiles: end.pageFilesUsable ? files : null, problem: end.problem, busy: null});
				return;
			}
			settledSessions.current.add(record.id);
			if (end.kind === "cancelled") {
				// Cancelled on the progress card: the transfer is voided and the session forgotten.
				setPhase({name: "create"});
			} else if (end.kind === "gone") {
				setPhase({name: "abandoned", reason: "gone"});
			} else {
				failedSend(e, service, {
					mode: "upload",
					expireAfter: continuedRetention(record),
					passwordProtected: record.display.passwordProtected,
					maxDownloads: record.display.maxDownloads,
					initialRecipientEmails: record.display.initialRecipientEmails,
				});
			}
		}
	};

	/**
	 * Claims the card's upload for this page, then continues it with `files`, or the files this page or the browser
	 * holds for it. Another page uploading it, or the upload no longer kept, ends here with the card saying so.
	 */
	const claimAndContinue = async (current: Extract<CreatePhase, {name: "resume"}>, files: File[] | null) => {
		const sessionId = current.kept.session.record.id;
		setPhase({...current, busy: "continuing"});
		let claim;
		try {
			const runtime = await import("../core/upload/resume/resumeRuntime");
			claim = await runtime.claimKeptUpload(sessionId);
		} catch {
			setPhase({...current, busy: null, problem: {kind: "failed"}});
			return;
		}
		if (claim.kind === "elsewhere") {
			setPhase({...current, busy: null, problem: {kind: "elsewhere"}});
			return;
		}
		if (claim.kind === "gone") {
			// Another page finished or discarded it meanwhile.
			settledSessions.current.add(sessionId);
			resumable.refresh();
			setPhase({name: "create"});
			return;
		}
		const atHand = files ?? current.pageFiles ?? claim.kept.keptFiles;
		if (atHand === null) {
			claim.release();
			setPhase({...current, kept: claim.kept, busy: null});
			return;
		}
		await continueUpload(claim, atHand);
	};

	const onResumeContinue = () => {
		if (phase.name !== "resume" || phase.busy !== null) return;
		void claimAndContinue(phase, null);
	};

	/** The sender picked the files again: continue with them when they are the same, else say which is not. */
	const onResumePicked = (picked: SelectedFile[]) => {
		if (phase.name !== "resume" || phase.busy !== null) return;
		const current = phase;
		void import("../core/upload/resume/resumeRuntime").then(runtime => {
			const match = runtime.matchPickedFiles(current.kept.session.record.files, picked);
			if (match.kind === "matched") {
				void claimAndContinue(current, match.files);
				return;
			}
			setPhase({...current, problem: {kind: "mismatch", firstMissing: match.firstMissing, missingCount: match.missingCount}});
		});
	};

	/**
	 * Discarded on the card, once confirmed: claimed for this page, its transfer voided on the api, and only then
	 * forgotten. A void that did not happen keeps the upload, and the card says it could not be discarded yet,
	 * unless the api says its transfer is gone or finished, which is forgotten and said (`afterDiscard`).
	 */
	const onConfirmDiscard = () => {
		if (phase.name !== "resume" || phase.busy !== null) return;
		const current = phase;
		const sessionId = current.kept.session.record.id;
		setDiscardConfirmOpen(false);
		setPhase({...current, busy: "discarding"});
		void (async () => {
			let claim;
			let outcome: DiscardOutcome | "notKept" = "notKept";
			try {
				const runtime = await import("../core/upload/resume/resumeRuntime");
				claim = await runtime.claimKeptUpload(sessionId);
				if (claim.kind === "claimed") {
					outcome = await runtime.forgetUpload(claim, ownerUidRef.current);
					if (outcome === "kept") {
						setPhase({...current, kept: claim.kept, busy: null, problem: {kind: "discardFailed"}});
						return;
					}
				}
			} catch {
				setPhase({...current, busy: null, problem: {kind: "discardFailed"}});
				return;
			}
			if (claim.kind === "elsewhere") {
				setPhase({...current, busy: null, problem: {kind: "elsewhere"}});
				return;
			}
			settledSessions.current.add(sessionId);
			resumable.refresh();
			const said = afterDiscard(outcome);
			setPhase(said === "compose" ? {name: "create"} : {name: "abandoned", reason: said});
		})();
	};

	// A kept upload is offered on the composer's own card, once the composer is empty and nothing else holds
	// the card: never over a selection the sender is composing, or over the email-code step (ui#3204). One
	// that has just been ended past saving is said once instead, when nothing is offered.
	useEffect(() => {
		if (phase.name !== "create" || selection.length > 0 || storedFiles !== null || codeLogin.phase.step === "confirm") {
			return;
		}
		const offer = resumable.offer;
		if (offer !== null && !settledSessions.current.has(offer.session.record.id)) {
			setPhase({name: "resume", kept: offer, pageFiles: null, problem: null, busy: null});
			return;
		}
		if (resumable.expired) {
			resumable.acknowledgeExpired();
			setPhase({name: "abandoned", reason: "expired"});
		}
	}, [resumable.offer, resumable.expired, phase.name, selection.length, storedFiles, codeLogin.phase.step]);

	const transferCreationEffects = {
		requestSignIn: codeLogin.request,
		// The objection is recorded around the confirm alone, never around the send that follows it: a send
		// that fails after the account exists must not drop that account's objection.
		confirmSignIn: (code: string) => codeStepObjection.around(() => codeLogin.confirm(code)),
		createTransfer: startTransfer,
		awaitSession: sessionWait.wait,
	};

	// One submit at a time (ui#2088): a Send held for the restored session keeps this flight open, so a
	// second press before the busy render lands joins the first instead of queueing a second creation
	// behind the same wait.
	const submitFlight = useRef<SingleFlight | null>(null);
	submitFlight.current ??= new SingleFlight();
	const onSubmit = (payload: TransferPayload) => submitFlight.current!.run(async () => {
		if (!validate(payload)) return;
		try {
			await advanceTransferCreation({
				kind: "submit",
				displayedUserIsLoggedIn: isLoggedIn,
				authenticatedSession: authenticatedSession.state,
				email: payload.emailsFrom,
				payload,
			}, transferCreationEffects);
		} catch (error) {
			const failure = classifySenderFailure(error, SenderFailureContext.Authentication);
			reportSenderFailure(SenderFailureContext.Authentication, failure);
			showErrorToast(senderFailureText(failure, sender.errors, sender.deliveryRetry.retryAfter));
		}
	});

	const onConfirmSignIn = async (code: string) => {
		await advanceTransferCreation({kind: "confirm", code}, transferCreationEffects);
	};

	/**
	 * A press of Request files, admitted by the rule every gated press follows (`settledCreationAction`,
	 * ui#3117): made at once on an authenticated session, held while a shown sign-in is still being
	 * restored, and otherwise turned into the send's own email step (kitecorp/transferify-ui#3140, owner
	 * 2026-10-01). The code makes the visitor's account; like Send, the request is made by the press after
	 * it (ui#246), from the draft the composer kept. The stored terms acceptance gates it as it gates a send.
	 */
	const admitRequest = async (email: string): Promise<boolean> => {
		if (!terms.confirmAccepted()) return false;
		try {
			const action = await settledCreationAction(isLoggedIn, authenticatedSession.state, sessionWait.wait);
			if (action === "requestSignIn") await codeLogin.request(email);
			return action === "create";
		} catch (error) {
			const failure = classifySenderFailure(error, SenderFailureContext.Authentication);
			reportSenderFailure(SenderFailureContext.Authentication, failure);
			showErrorToast(senderFailureText(failure, sender.errors, sender.deliveryRetry.retryAfter));
			return false;
		}
	};

	const onRetryDelivery = () => {
		if (phase.name !== "deliveryRetry") return;
		const resume = deliveryResumeRef.current;
		if (resume === null) return;
		const action = resume.retry();
		if (!action.started) return;
		const retryPhase = phase;
		setPhase({...retryPhase, busy: true});
		void (async () => {
			try {
				const response = await action.result;
				// The retried emails are what completes a hosted send that reached this screen.
				if (retryPhase.mode === "upload") {
					uploadGame.transferCompleted();
				}
				setPhase({
					name: "success",
					mode: retryPhase.mode,
					url: response.url,
					transferId: response.transferId,
					expiresAtMs: expiryDeadline(retryPhase.expireAfter, Date.now()),
					passwordProtected: retryPhase.passwordProtected,
					maxDownloads: retryPhase.maxDownloads,
					initialRecipientEmails: retryPhase.initialRecipientEmails,
					deliveryNotice: response.delivery,
				});
				const skipped = response.delivery === undefined
					? null
					: deliveryNoticeMessage(response.delivery, sender.success.delivery);
				if (skipped !== null) {
					showWarningToast(skipped);
				} else {
					showSuccessToast(retryPhase.storedLink ? sender.toasts.linkReady : sender.toasts.uploaded);
				}
			} catch (error) {
				const failure = classifySenderFailure(error, SenderFailureContext.Delivery);
				reportSenderFailure(SenderFailureContext.Delivery, failure);
				setPhase({...retryPhase, failure, busy: false});
			}
		})();
	};

	const onRequestStartOver = () => {
		if (phase.name !== "deliveryRetry" || phase.busy) return;
		const resume = deliveryResumeRef.current;
		if (resume === null) return;
		if (resume.requestStartOver()) {
			setStartOverConfirmOpen(true);
		}
	};

	const onCancelStartOver = () => {
		deliveryResumeRef.current?.cancelStartOver();
		setStartOverConfirmOpen(false);
	};

	const onConfirmStartOver = () => {
		if (phase.name !== "deliveryRetry" || phase.busy) return;
		const resume = deliveryResumeRef.current;
		if (resume === null || resume.confirmStartOver() !== "idle") return;
		setStartOverConfirmOpen(false);
		// Starting over abandons a transfer that was already CREATED and whose personalization the api has
		// already taken, so its draft is as stale here as after an ordinary send.
		forgetPersonalization();
		senderChoice.reset();
		selectionRef.current = [];
		setSelection([]);
		setStoredFiles(null);
		setPhase({name: "create"});
	};

	/**
	 * Progress-screen Cancel: abort the part PUTs, then void the transfer server-side (best-effort).
	 * The pending upload() then rejects with UploadAbortedError, which resets the phase above —
	 * a single exit path, so a cancel can never race a late success transition.
	 *
	 * The void is the upload clients' own, on that rejection path (`voidCancelledTransfer`), and it is
	 * what returns an over-limit purchase the init had already spent (ui#1788). This comment claimed the
	 * transfer was voided long before anything did it: the api exposed no transfer-level abort, so a
	 * cancelled paid send left the row behind holding a purchase nobody could hand back.
	 */
	const onCancel = () => {
		// On the progress screen the service is already loaded (upload() ran through it), so this
		// resolves immediately.
		void transfer().then(t => t.cancelUpload()).catch(() => {
			// Server-side void failed; the local abort already stopped the upload.
		});
	};

	// Reset the draft: the next transfer starts with a plain page.
	const forgetPersonalization = useCallback(() => {
		personalise.reset();
		setBuilderAppeared(false);
		sentPersonalization.current = null;
	}, [personalise]);

	// A new transfer, so a new choice of who sends it. Not in `forgetPersonalization`, which also runs after every
	// request, whose next request keeps the choice.
	const onSendAnother = () => {
		forgetPersonalization();
		senderChoice.reset();
		setStoredFiles(null);
		changeSelection([]);
		setPhase({name: "create"});
	};

	/**
	 * The Requested tab's new request (kitecorp/transferify-ui#3140) asks for request mode and moves the
	 * tab home. This flow takes the ask as it is uncovered, wherever it is (`composerArrival`): a send's
	 * success card closes as Send another closes it, and an upload or a delivery retry keeps the ask until
	 * the sender is back on the composer, whose panel then opens request mode and spends it. The reply
	 * composer a download page hosts never takes it, since it offers no request mode.
	 */
	const [requestModeOwed, setRequestModeOwed] = useState(false);
	useEffect(() => {
		if (covered || replyActive || !requestModeRequests.consume(performance.now())) return;
		if (composerArrival(phase.name) === "closeSuccessThenOpen") onSendAnother();
		setRequestModeOwed(true);
		// Only an uncovering or a new phase can let an ask in; `onSendAnother` is read from the render that ran.
	}, [covered, replyActive, phase.name]);
	const spendRequestModeOwed = useCallback(() => setRequestModeOwed(false), []);

	/**
	 * The Files tab's "New transfer with these files" (kitecorp/transferify-ui#3405) hands its files over and moves
	 * the tab home. Taken by the same rule as the request-mode ask (`composerArrival`): a send's success card
	 * closes first, and an upload or a delivery retry keeps the files until the sender is back on the composer.
	 * They replace whatever the composer held: a new link cannot carry an upload beside it.
	 */
	const [storedOwed, setStoredOwed] = useState<readonly StoredFile[] | null>(null);
	useEffect(() => {
		if (covered || replyActive) return;
		const asked = storedFilesRequests.consume(performance.now());
		if (asked === null) return;
		if (composerArrival(phase.name) === "closeSuccessThenOpen") onSendAnother();
		setStoredOwed(asked);
		// Only an uncovering or a new phase can let an ask in; `onSendAnother` is read from the render that ran.
	}, [covered, replyActive, phase.name]);
	useEffect(() => {
		if (storedOwed === null || phase.name !== "create") return;
		changeSelection([]);
		setStoredFiles(storedOwed);
		setStoredOwed(null);
		// `changeSelection` is read from the render that ran; the files and the phase are what admit them.
	}, [storedOwed, phase.name]);

	// Stable identity so the instant success screen subscribes exactly once per mount. The success
	// screen only renders after a successful upload(), which awaited transfer() — so the service is
	// always instantiated here and the Unsubscribe can be returned synchronously.
	const subscribePeerActivity = useCallback(
		(listener: PeerActivityListener) => {
			const service = transferRef.current;
			return service ? service.onPeerActivity(listener) : () => {};
		},
		[],
	);

	// Who owns the create card right now (ui#1549). Three surfaces compete for one box and the answer
	// is a rule, not markup — see {@link ./dropAreaGate}, which is where "the gate covers, the verify
	// step replaces" is stated and tested without rendering anything.
	const dropArea = resolveDropArea({
		covered,
		consentOpen: terms.open,
		verifying: codeLogin.phase.step === "confirm",
	});
	// The drop area with nothing picked yet, which the card pads with its own inset.
	const emptyCard = phase.name === "create" && panelFiles.length === 0 && !dropArea.panelHidden;

	return (
		<FlowRoot ref={ref} data-testid="create-flow">
			<VisuallyHiddenLiveRegion
				data-testid="create-email-code-announcement"
				role="status"
				aria-live="polite"
				aria-atomic="true"
			>
				{codeAnnouncementMessage}
			</VisuallyHiddenLiveRegion>
			<PageDropLayer
				selection={selection}
				onSelectionChange={onSelectionChange}
				onLosses={onSelectionLosses}
				enabled={!covered && phase.name === "create" && storedFiles === null}
			>
				<Backdrop $success={phase.name === "success"}>
					<SuccessGiftFrame active={phase.name === "success"} composing={slideOutCandidates} composingBeside={besideOfferCandidates} mode={phase.name === "success" ? phase.mode : undefined}
						completedSendTransferId={phase.name === "success" ? couponTransferId(phase.transferId ?? null) : null}
						subscribeActivity={subscribePeerActivity}>
					<Card data-create-card $empty={emptyCard}
						$composing={phase.name === "create" && panelFiles.length > 0 && !dropArea.panelHidden}
						$success={phase.name === "success"}>
						{/* ui#132: the verify step replaces the panel INSIDE the card flow; the panel stays
						  * mounted (hidden) so "use a different email" returns with the form state intact. */}
						{phase.name === "create" && (
							<PanelSlot $hidden={dropArea.panelHidden}>
									<TransferPanel
										replyTarget={replyTarget}
										replyActive={replyActive}
										slideOutHost={slideOutHost}
										besideOfferHost={besideOfferHost}
									covered={!dropArea.panelInteractive}
									files={panelFiles}
									storedFiles={storedFiles !== null}
									fileList={fileList}
									onSubmit={onSubmit}
									onRemoveFile={onRemoveFile}
									isSeeding={false}
									authInFlight={codeLogin.busy || sessionWait.waiting || linkingStored}
									signInStepOpen={codeLogin.phase.step === "confirm"}
									activeStorageRefused={activeStorageRefused}
									planRequiredRefused={planRequiredRefused}
									onPlanRefusalHandled={() => setPlanRequiredRefused(null)}
									personalise={personalise}
									senderChoice={senderChoice}
									onBuilderOpened={openBuilderFromPanel}
									onBuilderCommitted={countBuilderCommitted}
									requestModeOwed={requestModeOwed}
									onRequestModeOwedSpent={spendRequestModeOwed}
									onRequestLookSpent={forgetPersonalization}
									admitRequest={admitRequest}
								/>
							</PanelSlot>
						)}
						{/* ui#1170 put the consent gate inside the drop area; ui#1549 stopped it REPLACING
						  * the panel; ui#1575 took it out of the card's FLOW as well; ui#1728 makes it fill
						  * the card. The gate is raised over the panel — owner, 2026-08-27: "it should be on
						  * top of the content and not mess with it's content" — so the drop area holds the
						  * same box whether the gate is up or not (see {@link ./CreateFlow.styles.ConsentDock}).
						  * Rendered AFTER the panel so the announced order matches the seen order; the dock's
						  * own inset is what places it, not this position. A sender who has not accepted is
						  * still stopped — `dropArea.panelInteractive` is false, which makes the whole control
						  * block inert, so nobody uploads without accepting — and the panel was already only
						  * HIDDEN here, never unmounted, so files dropped on the page while the gate is up are
						  * still in the draft afterwards.
						  *
						  * ui#1549's separate wash layer is gone with ui#1728. It dimmed the panel so a visitor
						  * could still read the card behind a translucent gate; an OPAQUE takeover leaves it
						  * nothing to dim, so keeping it would have meant painting a layer no state can show. */}
						{phase.name === "create" && dropArea.consentVisible && (
							<ConsentDock>
								<Consent covered={covered} onAgree={terms.accept}/>
							</ConsentDock>
						)}
						{phase.name === "create" && codeLogin.phase.step === "confirm" && (
							<VerificationStep
								email={codeLogin.phase.email}
								requestedAt={codeLogin.requestedAt}
								deliveryMessage={codeDeliveryMessage}
								error={codeLoginError}
								busy={codeLogin.busy}
								resendDisabled={codeLogin.resendDisabled}
								onConfirm={code => void onConfirmSignIn(code)}
								onResend={() => void codeLogin.resend()}
								onDismiss={codeLogin.reset}
								objection={codeStepObjection}
							/>
						)}
						{phase.name === "progress" && (
							<ProgressCard mode={phase.mode} files={rawFiles} progress={progress}
								readout={uploadReadout} onCancel={onCancel} game={uploadGame}
								completedWhilePlaying={heldSuccess !== null} onContinue={continueToSuccess}
								accentColor={effectivePersonalizationAccent(personalise.draft, personalise.inherited)}
								connection={connection} continuable={continuable}/>
						)}
						{phase.name === "resume" && (
							<ResumeUploadCard
								summary={sessionSummary(phase.kept.session)}
								filesAtHand={(phase.pageFiles ?? phase.kept.keptFiles) !== null}
								problem={phase.problem}
								busy={phase.busy}
								onContinue={onResumeContinue}
								onPicked={onResumePicked}
								onDiscard={() => setDiscardConfirmOpen(true)}
							/>
						)}
						{phase.name === "abandoned" && (
							<AbandonedUploadCard reason={phase.reason} onStartNew={() => setPhase({name: "create"})}/>
						)}
						{phase.name === "deliveryRetry" && (
							<DeliveryRetryRoot
								aria-labelledby="delivery-retry-title"
								aria-busy={phase.busy}
							>
								<DeliveryRetryTitle id="delivery-retry-title">
									{sender.deliveryRetry.title}
								</DeliveryRetryTitle>
								<DeliveryRetryCopy>{sender.deliveryRetry.body}</DeliveryRetryCopy>
								<DeliveryErrorDetail role="alert" aria-live="assertive">
									{senderFailureText(phase.failure, sender.errors, sender.deliveryRetry.retryAfter)}
								</DeliveryErrorDetail>
								<DeliveryRetryActions>
									<RetryDeliveryButton type="button" disabled={phase.busy} onClick={onRetryDelivery}>
										{phase.busy ? sender.deliveryRetry.retrying : sender.deliveryRetry.retry}
									</RetryDeliveryButton>
									<StartOverButton type="button" disabled={phase.busy} onClick={onRequestStartOver}>
										{sender.deliveryRetry.startOver}
									</StartOverButton>
								</DeliveryRetryActions>
							</DeliveryRetryRoot>
						)}
						{phase.name === "success" && (
							<SuccessCard
								personaliseAction={successPersonalizationEntry({
									transferId: phase.transferId,
									personalized: personalizationRungSettles(sentPersonalization.current?.rung ?? null),
									expiresAtMs: phase.expiresAtMs, now: Date.now()})
									? <SuccessPersonalization transferId={phase.transferId!}
										attached={sentPersonalization.current?.request ?? null}
										company={sentCompany.current}
										fileCount={storedFiles?.length ?? selection.length}/>
									: null}
								mode={phase.mode}
								url={phase.url}
								expiresAtMs={phase.expiresAtMs}
								passwordProtected={phase.passwordProtected}
								maxDownloads={phase.maxDownloads}
								initialRecipientEmails={phase.initialRecipientEmails}
								deliveryNotice={phase.deliveryNotice}
								hostedGame={uploadGame}
								onSendAnother={onSendAnother}
								subscribePeerActivity={subscribePeerActivity}
							/>
						)}
					</Card>
					</SuccessGiftFrame>
				</Backdrop>
			</PageDropLayer>
			<BaseDialog
				title={sender.resume.confirmDiscard.title}
				open={discardConfirmOpen && phase.name === "resume"}
				onClose={() => setDiscardConfirmOpen(false)}
				scroll="paper"
				aria-describedby="resume-discard-description"
			>
				<DialogContent>
					<Typography id="resume-discard-description" variant="body2" color="text.secondary">
						{sender.resume.confirmDiscard.body}
					</Typography>
				</DialogContent>
				<PickerDialogActions>
					<Button autoFocus onClick={() => setDiscardConfirmOpen(false)}>{common.cancel}</Button>
					<Button variant="contained" color="error" disabled={phase.name !== "resume" || phase.busy !== null}
						onClick={onConfirmDiscard}>
						{sender.resume.confirmDiscard.confirm}
					</Button>
				</PickerDialogActions>
			</BaseDialog>
			<BaseDialog
				title={sender.deliveryRetry.confirmStartOver.title}
				open={startOverConfirmOpen && phase.name === "deliveryRetry"}
				onClose={onCancelStartOver}
				scroll="paper"
				aria-describedby="delivery-start-over-description"
			>
				<DialogContent>
					<Typography id="delivery-start-over-description" variant="body2" color="text.secondary">
						{sender.deliveryRetry.confirmStartOver.body}
					</Typography>
				</DialogContent>
				<PickerDialogActions>
					<Button autoFocus onClick={onCancelStartOver}>{common.cancel}</Button>
					<Button
						variant="contained"
						color="error"
						disabled={phase.name !== "deliveryRetry" || phase.busy}
						onClick={onConfirmStartOver}
					>
						{sender.deliveryRetry.confirmStartOver.confirm}
					</Button>
				</PickerDialogActions>
			</BaseDialog>
		</FlowRoot>
	);
});

export default CreateFlow;
